import type { GetStaticPaths } from "next"
import Image from "next/image"
import { Anchor } from "../../app/components/anchor"
import { useRouter } from "next/router"
import { AiFillCheckCircle } from "react-icons/ai"
import { GoCheckCircle } from "react-icons/go"
import { ReviewsContextProvider } from "../../context/ReviewsContext"
import Navbar from "../../components/navbar"
import PageHead from "../../components/head/PageHead"
import Layout from "../../components/layout"
import BreedStarRating from "../../components/BreedStarRating"
import RatingCard from "../../components/review-rating-card"
import { PetBreedHeroForm } from "../../components/pet-breed/hero"
import { DogBreeds, DogBreed } from "../../components/pet-breed/dog-breeds"
import Breadcrumb from "../../components/breadcrumbs"
import BreedHeroImages, {
  IHeroImageData,
} from "../../components/pet-breed/dynamic-hero-images"

import Cursor from "../../assets/vector/cursor-arrow-rays.svg"
import Badge from "../../assets/vector/check-badge.svg"
import Distance from "../../assets/vector/miles.svg"
import Truck from "../../assets/vector/truck.svg"
import Union from "../../assets/vector/unions.svg"
import Arrows from "../../assets/vector/arrows-pointing-out.svg"
import Map from "../../assets/vector/map.svg"
import Plane from "../../assets/vector/plane.svg"
import Globe from "../../assets/vector/globe-americas.svg"
import Pawprint from "../../assets/images/pawprint.png"
import Shadow from "../../assets/images/shadow.png"

import dogsData from "../../constants/pet-breed/dogs.json"
import heroImages from "../../constants/pet-breed/breeds-images.json"
import transportationData from "../../constants/pet-breed/all-breeds.json"
import { urlifyName } from "../../constants/stateCityDictionary"
import Footer from "../../storyblok/static/Footer"

import { shuffleArray } from "../../util/array"
import { localizeNumber } from "../../util/formatNumber"
import { getCityUrl } from "../../util/getCityUrl"
import { redactNames } from "../../util/string"
import { isValidDriverId } from "@/util/validateDriver"

const navbarActiveButtons = (shipmentFormLink: string) => [
  {
    text: "Get Transportation Quotes",
    route: shipmentFormLink,
  },
]

const navbarSideButtons = [
  {
    text: "How it works",
    route: "/how-it-works",
  },
  {
    text: "Log in",
    route: "/login",
    isReactLink: true,
  },
]

const supplies: string[] = [
  "Food, bowls and fresh water",
  "Collars and leashes",
  "Waste bags",
  "Medication",
  "First-aid supplies",
  "Any travel documents",
  "A secure, USDA-approved travel crate",
]

const tableHeadings = ["Shipment", "Location", "Distance", "Cost"]
const PetBreed = ({
  breeds,
  shipmentsCost,
  dogTransporters,
  pageTitle,
  pageDescription,
  abfHeading,
  abfSubheading,
  heroImageData,
}: {
  breeds: DogBreed
  shipmentsCost: { [key: string]: string }[]
  dogTransporters: { [key: string]: string }[]
  pageTitle: string
  pageDescription: string
  abfHeading: string
  abfSubheading: string
  heroImageData?: IHeroImageData
}) => {
  const router = useRouter()
  const { breed } = router.query

  if (!breed) {
    return <div>Loading...</div>
  }

  const headerContent = {
    title: pageTitle,
    metaTags: [
      {
        id: 1,
        content: pageDescription,
        name: "description",
      },
    ],
    showZendeskHelp: false,
  }

  return (
    <ReviewsContextProvider>
      <PageHead title={pageTitle} description={pageDescription} />
      <Layout {...headerContent}>
        <div>
          <Navbar
            activeButton={navbarActiveButtons("/shipments/new/animals/dogs")}
            sideButton={navbarSideButtons}
            fixedNavigation
          />
          <section id="page-hero" className="pt-20  bg-gradient3 ">
            <div className="flex gap-10 lg:gap-14 max-w-[1289px] mx-auto pb-10 mt-10">
              <div className="w-full md:w-2/5">
                <div className="pb-8">
                  <Breadcrumb
                    items={[
                      { name: "Home", href: "/" },
                      {
                        name: "Dog Transportation",
                        href: "/dog-transportation",
                      },
                      {
                        name: "All Breeds",
                        href: "/dog-shipping/all-breeds",
                      },
                      { name: breeds.breed, href: "/all-breeds" },
                    ]}
                  />
                </div>
                <div className="h-fit bg-white p-6 rounded-xl space-y-6 drop-shadow">
                  <h1 className="m-0 text-blue7 text-3xl lg:text-[44px] lg:leading-[52px] font-semibold font-montserrat">
                    {abfHeading ||
                      `${breeds.breed} ${breeds.animal} Transport Service`}
                  </h1>
                  <div className="md:hidden flex items-center justify-center relative h-full">
                    <Image
                      className="absolute w-[321px] h-[214px] sm:max-w-[70%] object-cover object-top rounded-2xl "
                      src={heroImageData?.horizontal_image_url || ""}
                      alt={heroImageData?.horizontal_image_alt_text || "Dog"}
                      height={214}
                      layout="responsive"
                      width={321}
                    />
                    <Image
                      className="max-w-[45%] sm:max-w-[30%] w-full object-cover object-top"
                      src={Pawprint.src}
                      alt="paw print"
                      layout="responsive"
                      height={469}
                      width={180}
                    />
                    <Image
                      className="absolute w-[32px] h-[42px] max-w-[16%] sm:max-w-[15%] -right-10  sm:right-10 bottom-12 sm:bottom-10"
                      src={Shadow.src}
                      alt="paw print"
                      height={30}
                      layout="responsive"
                      width={32}
                    />
                  </div>
                  <h2 className="text-lg font-normal text-blue5">
                    {abfSubheading ||
                      `The easy, safe and stress-free way to transport your ${breeds.breed}. Find trusted drivers in just minutes!`}
                  </h2>

                  <PetBreedHeroForm />
                  <div dir="ltr">
                    <aside className="mt-3 sm:mt-6">
                      <RatingCard />
                    </aside>
                  </div>
                </div>
              </div>
              <div className="hidden md:block md:w-3/5">
                <BreedHeroImages heroImageData={heroImageData} />
              </div>
            </div>
          </section>
          <section
            id="stats"
            className="mt-10 md:mt-20 pt-20 max-w-[1289px] mx-auto px-4 md:px-1"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-8 mb-4 max-w-[1350px] mx-auto">
              <div className="flex items-center">
                <Image
                  src={Globe.src}
                  alt={"rating"}
                  height={48}
                  width={48}
                  loading="lazy"
                />
                <div className="pl-2">
                  <p className="mb-0.5">2,500+ drivers</p>
                  <span className="text-sm">
                    Actively competing for your business.
                  </span>
                </div>
              </div>
              <div className="flex items-center">
                <Image
                  src={Cursor.src}
                  alt={"rating"}
                  height={48}
                  width={48}
                  loading="lazy"
                />
                <div className="pl-2">
                  <p className="mb-0.5">90% of listings</p>
                  <span className="text-sm">
                    Receive a quote in less than 10 min.
                  </span>
                </div>
              </div>
              <div className="flex items-center">
                <Image
                  src={Map.src}
                  alt={"rating"}
                  height={48}
                  width={48}
                  loading="lazy"
                />
                <div className="pl-2">
                  <p className="mb-0.5">100,000,000+</p>
                  <span className="text-sm">
                    Miles driven by qualified transporters.
                  </span>
                </div>
              </div>
              <div className="flex items-center">
                <Image
                  src={Badge.src}
                  alt={"rating"}
                  height={48}
                  width={48}
                  loading="lazy"
                />
                <div className="pl-2">
                  <p className="mb-0.5">14+ years experience</p>
                  <span className="text-sm">Shipping what matters most.</span>
                </div>
              </div>
            </div>
          </section>
          <section
            id="table"
            className="mt-6 max-w-[1289px] mx-auto px-4 md:px-1"
          >
            <div className="overflow-x-auto">
              <div className="max-auto md:text-center w-full mb-7 mt-10 pt-8">
                <h2 className="font-semibold mb-6 font-montserrat md:text-[32px] md:leading-10 ">
                  How much does it cost to transport your dogs
                </h2>
                <p className="mb-4 text-center mx-auto max-w-4xl">
                  Your {breeds?.animal} is so much more than four legs and a
                  tail — they’re members of your family and best friends for
                  life. We’re proud to be the nation’s top-rated and leading
                  service for {breeds?.animal} transportation.{" "}
                </p>
                <p className="mb-10 text-center mx-auto max-w-4xl">
                  {" "}
                  Our unique process matches you with a fleet of pre-screened
                  drivers with prior experience at transporting dogs, with
                  strong emphasis on your pet's comfort and safety.
                </p>
              </div>
              <h3 className="text-xl md:text-center  font-semibold text-gray-900 mb-4">
                Successful {breeds.breed} transports from our marketplace
              </h3>
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="w-full h-11 text-base border-gray-300 py-8 bg-blue26">
                    {tableHeadings.map((item) => (
                      <th
                        key={item}
                        className="uppercase text-left font-medium text-gray-600 px-2 md:px-6 py-2 text-sm md:text-base"
                      >
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shipmentsCost.map((shipment, index) => (
                    <tr className="h-11 border-gray-300 border-b" key={index}>
                      <td className="text-left px-1 md:px-6 py-2 text-sm md:text-base">
                        {redactNames(shipment.member_f_name ?? "")} shipped{" "}
                        <span className="font-bold">
                          {shipment.animal_name}
                        </span>
                      </td>
                      <td className="text-left px-1 md:px-6 py-2 text-sm md:text-base">
                        {getCityUrl(shipment.from) ? (
                          <Anchor href={getCityUrl(shipment.from) ?? ""}>
                            {shipment.from}
                          </Anchor>
                        ) : (
                          shipment.from
                        )}{" "}
                        →{" "}
                        {getCityUrl(shipment.to) ? (
                          <Anchor href={getCityUrl(shipment.to) ?? ""}>
                            <b>{shipment.to}</b>
                          </Anchor>
                        ) : (
                          <b>{shipment.to}</b>
                        )}
                      </td>
                      <td className="text-left px-1 md:px-6 py-2">
                        <span className="bg-blue16 rounded-full px-1.5 py-0.5 md:px-2 md:py-2 text-xs md:text-base inline-flex items-center">
                          <Image
                            src={Distance.src}
                            alt={"rating"}
                            height={26}
                            width={24}
                            loading="lazy"
                            className="w-[28px] h-[28px]"
                          />
                          {Number(shipment.distance).toLocaleString("en-US")} mi
                        </span>
                      </td>
                      <td className="text-left px-1 md:px-6 py-4 text-blue7 text-sm md:text-base">
                        {localizeNumber(shipment.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section
            id="transporters"
            className="mt-6 max-w-[1289px] mx-auto px-4 md:px-1"
          >
            <h3 className="max-w-[40ch] pt-16 mx-auto mb-16 font-montserrat font-medium text-3xl md:text-4xl leading-[44px] text-center text-blue7">
              We are committed to building a safe, caring environment for people
              and animals on the road
            </h3>
            <div className="grid lg:grid-cols-[446px_440px] gap-4 md:gap-6 lg:gap-11 max-auto justify-center">
              <div className="max-w-[446px]">
                <div className="border-gray52 border rounded">
                  <h4 className="p-4 bg-blue16 flex items-center">
                    Top {breeds.breed} transporters
                  </h4>

                  {dogTransporters.map(
                    ({
                      photo_url,
                      transporter_name,
                      id,
                      avg_rating,
                      transporter_id,
                    }) => {
                      const roundedValue =
                        Math.round(Number(avg_rating) * 10) / 10
                      return (
                        <div
                          className="p-6 border-b last-of-type:border-none flex"
                          key={id}
                        >
                          <Image
                            src={photo_url}
                            alt={`${id} reviews`}
                            className="h-14 w-14 mr-4 rounded-full overflow-hidden object-cover"
                            width={56}
                            height={56}
                          />
                          <div>
                            <p className="mb-0 text-lg">
                              {isValidDriverId(transporter_id) ? (
                                <Anchor
                                  href={`https://citizenshipper.com/drivers/${transporter_id}`}
                                >
                                  {transporter_name}
                                </Anchor>
                              ) : (
                                transporter_name
                              )}
                            </p>
                            <div className="flex items-center text-base font-work_sans">
                              <BreedStarRating value={Number(avg_rating)} />
                              <span className="ml-3">{roundedValue}/5</span>
                            </div>
                          </div>
                        </div>
                      )
                    },
                  )}
                </div>
              </div>
              <div className="">
                <ul className="px-0">
                  <li className="text-xl py-3 leading-7 flex items-center">
                    <span className="text-green5 pr-6">
                      <GoCheckCircle size={36} />
                    </span>{" "}
                    We carefully background check all drivers.
                  </li>
                  <li className="text-xl py-3 leading-7 flex items-center">
                    <span className="text-green5 pr-6">
                      <GoCheckCircle size={36} />
                    </span>{" "}
                    We verify transporter addresses and contact information for
                    additional protection.
                  </li>
                  <li className="text-xl py-3 leading-7 flex items-center">
                    <span className="text-green5 pr-6">
                      <GoCheckCircle size={36} />
                    </span>{" "}
                    Photos of drivers and their vehicles help confirm who and
                    what to expect.
                  </li>
                  <li className="text-xl py-3 leading-7 flex items-center">
                    <span className="text-green5 pr-6">
                      <GoCheckCircle size={36} />
                    </span>{" "}
                    Detailed customer reviews provide insight and transparency
                    to your selection process.
                  </li>
                  <li className="mt-10">
                    {" "}
                    <Anchor
                      href={"/shipments/new/animals/dogs"}
                      className="w-full md:w-auto px-4 md:px-16 rounded-md py-5 text-center bg-blue7 hover:text-white hover:no-underline text-white text-lg"
                    >
                      Get Pet Transportation Quotes
                    </Anchor>
                  </li>
                </ul>
              </div>
            </div>
          </section>
          <section className="mt-10 md:mt-20" id="shopping-cost">
            <div className="bg-blue16 p-4 md:p-6 rounded-lg shadow-lg">
              <div className="max-auto md:text-center w-full mb-7 mt-10 pt-8">
                <h2 className="font-semibold mb-3 font-montserrat text-[32px] leading-10 ">
                  What determines the cost of {breeds.breed} shipping
                </h2>
                <p className="mn-6 md:mb-10">
                  Here's what you need to know about safely transporting your{" "}
                  {breeds.breed}.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4 max-w-[1350px] mx-auto">
                <div className="bg-white rounded-xl px-3 md:px-6 py-5">
                  <h3 className="flex items-center font-semibold mb-5 font-montserrat text-xl md:text-2xl">
                    <div className="bg-blue16 rounded-full p-2 mr-2">
                      <Image
                        src={Truck.src}
                        alt={"rating"}
                        height={28}
                        width={28}
                        loading="lazy"
                        className="w-[28px] h-[28px]"
                      />
                    </div>{" "}
                    Mode of transportation
                  </h3>
                  <div className="md:flex justify-between items-center gap-4 mb-3">
                    <div className="bg-blue16 rounded-lg p-4 w-full h-24 mb-4 md:mb-0">
                      <p className="mb-2 text-base flex items-center">
                        <span className="mr-2">
                          {" "}
                          <Image
                            src={Truck.src}
                            alt={"rating"}
                            height={24}
                            width={24}
                            loading="lazy"
                            className="w-[24px] h-[24px]"
                          />
                        </span>
                        Ground travel
                      </p>
                      <div className="flex items-center gap-1">
                        <p className="text-gray-600 text-sm md:text-base bg-white py-0.5 px-1 md:px-2 rounded">
                          Cost $<span className="text-gray-400">$$</span>
                        </p>
                        <p className="text-gray-600 text-sm md:text-base bg-white py-0.5 px-1 md:px-2 rounded">
                          Time ⏱⏱<span className="text-gray-300">⏱</span>
                        </p>
                      </div>
                    </div>
                    <div className="bg-blue16 rounded-lg p-4 w-full h-24">
                      <p className="mb-2 text-base flex items-center">
                        <span className="mr-2">
                          {" "}
                          <Image
                            src={Plane.src}
                            alt={"rating"}
                            height={24}
                            width={24}
                            loading="lazy"
                            className="w-[24px] h-[24px]"
                          />
                        </span>
                        Air travel
                      </p>
                      <div className="flex items-center gap-1">
                        <p className="text-gray-600 text-sm md:text-base bg-white py-0.5 px-1 md:px-2 rounded">
                          Cost $$$
                        </p>
                        <p className="text-gray-600 text-sm md:text-base bg-white py-0.5 px-1 md:px-2 rounded">
                          Time ⏱<span className="text-gray-300">⏱⏱</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    Ground and air travel have different associated costs.
                    Flying is typically the fastest option, but also the most
                    expensive and taxing on your pet.
                  </div>
                </div>

                {/* Travel distance */}
                <div className="bg-white rounded-xl px-3 md:px-6 py-5">
                  <h3 className="flex items-center font-semibold mb-5 font-montserrat text-xl md:text-2xl">
                    <div className="bg-blue16 rounded-full mr-2 p-2">
                      <Image
                        src={Distance.src}
                        alt={"rating"}
                        height={32}
                        width={32}
                        loading="lazy"
                        className="w-[32px] h-[32px]"
                      />
                    </div>{" "}
                    Travel distance
                  </h3>
                  <p className="text-gray-600 leading-6">
                    The further you need to transport your pet, the more it'll
                    cost. If you're using ground transportation, you'll have to
                    factor in fuel costs, lodging, meals on the road and
                    wear-and-tear on your vehicle. These costs are applicable
                    whether you drive the pet yourself or you hire a transporter
                    through CitizenShipper. Airlines charge more for longer
                    plane rides as well. This is due to the cost of fuel and the
                    need compensate pilots and flight attendants.
                  </p>
                </div>
                {/* Pet size */}
                <div className="bg-white rounded-xl px-3 md:px-6 py-5">
                  <h3 className="flex items-center font-semibold mb-5 font-montserrat text-xl md:text-2xl">
                    <div className="bg-blue16 rounded-full  p-2 mr-4">
                      <Image
                        src={Arrows.src}
                        alt={"rating"}
                        height={28}
                        width={28}
                        loading="lazy"
                        className="w-[28px] h-[28px]"
                      />
                    </div>{" "}
                    Pet size
                  </h3>
                  <p className="text-gray-600">
                    Smaller pets are easier – and less expensive – to transport.
                    If you’re flying your {breeds.breed}, they’ll need to travel
                    in the cargo hold if they’re over 20 pounds. You’ll also
                    need to buy a USDA-approved crate, which can cost around
                    $200. With ground transport, it’s easier for drivers to
                    accommodate small pets.
                  </p>
                </div>

                {/* Health clearance and travel equipment */}
                <div className="bg-white rounded-xl px-3 md:px-6 py-5">
                  <h3 className="flex items-center font-semibold mb-5 font-montserrat text-xl md:text-2xl">
                    <div className="bg-blue16 rounded-full p-2 mr-4">
                      <Image
                        src={Union.src}
                        alt={"rating"}
                        height={28}
                        width={28}
                        loading="lazy"
                        className="w-[28px] h-[28px]"
                      />
                    </div>{" "}
                    Health clearance and travel equipment
                  </h3>
                  <p className="text-gray-600">
                    The first step to transporting your {breeds.breed} is
                    ensuring they’re healthy enough to travel. Most species
                    require a vet-issued health certificate to cross state
                    lines. This will cost $50 to $300. You’ll likely need to
                    purchase some equipment too – such as a crate and travel
                    supplies.
                  </p>
                </div>
              </div>

              <div className="mx-auto w-full block text-center mt-16">
                <Anchor
                  href={"/shipments/new/animals/dogs"}
                  className="w-full md:w-auto px-4 md:px-16 rounded-md py-5 text-center bg-blue7 hover:bg-blue-700 mt-5 text-white hover:text-white text-md hover:no-underline"
                >
                  Calculate Your Estimate Now
                </Anchor>
                <aside className="mt-10 mb-3 flex flex-wrap justify-center items-center">
                  <div className="flex items-center">
                    <AiFillCheckCircle className="h-4 w-4 mr-2 fill-green5" />
                    <p className="m-0 mr-4">Fast and easy</p>
                  </div>
                  <div className="flex items-center">
                    <AiFillCheckCircle className="h-4 w-4 mr-2 fill-green5" />
                    <p className="m-0 mr-4">Only pay when you book</p>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          <DogBreeds
            breeds={breeds as DogBreed}
            supplies={supplies}
            heroImageData={heroImageData}
          />

          <Footer />
        </div>
      </Layout>
    </ReviewsContextProvider>
  )
}
export const getStaticPaths: GetStaticPaths = async () => {
  const paths = dogsData.map((pet: { breed: string }) => ({
    params: { breed: urlifyName(pet.breed) },
  }))

  return { paths, fallback: "blocking" }
}

export const getStaticProps = async ({
  params,
}: {
  params: { breed: string }
}) => {
  const { breed } = params

  const breeds = dogsData.find(
    (dog: { breed: string }) => urlifyName(dog.breed) === breed,
  )
  const heroImageData = heroImages.find(
    (item) => urlifyName(item.breed) === breed,
  )

  if (!breeds) {
    return { notFound: true }
  }

  const shipmentsCost = shuffleArray(
    transportationData.transportations_dogs.filter(
      (item) => urlifyName(item.breed) === breed,
    ),
  ).slice(0, 10)
  const dogTransporters = shuffleArray(
    transportationData.drivers_dogs.filter(
      (item) => urlifyName(item.breed) === breed,
    ),
  ).slice(0, 6)

  return {
    props: {
      breeds,
      shipmentsCost,
      dogTransporters,
      pageTitle:
        breeds.page_title ||
        `${breeds.breed} Shipping - Safe and Reliable ${breeds.breed} Transport Service`,
      pageDescription:
        breeds.meta_description ||
        `Reliable ${breeds.breed} shipping services in the US helps you find a trusted transporter for your ${breeds.breed} in just minutes.`,
      abfHeading: breeds.abf_heading || null,
      abfSubheading: breeds.abf_subheading || null,
      heroImageData,
    },
  }
}

export default PetBreed
